import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, Eye, CalendarRange } from "lucide-react";

function DailyBlock() {
  const [date, setDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const params = date ? { date } : {};
      const { data } = await api.post("/admin/daily-revenue-summary/preview", null, { params });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm(
      `Send DAILY summary NOW to: ${(preview?.recipients || ["admin@ifweknewaudio.com"]).join(", ")}?`
    )) return;
    setSending(true);
    try {
      const params = date ? { date } : {};
      const { data } = await api.post("/admin/daily-revenue-summary/send-test", null, { params });
      if (data?.ok) toast.success(`Daily sent to ${data.results?.length || 0} recipient(s)`);
      else toast.error(data?.reason || "Send failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const s = preview?.summary;
  return (
    <div className="border border-white/10 bg-coal p-5" data-testid="daily-block">
      <div className="flex items-center gap-2 mb-1">
        <Mail size={16} className="text-hotpink" />
        <h3 className="brush-title text-2xl text-white">Daily · 9 AM ET</h3>
      </div>
      <p className="scribble text-white/50 text-xs mb-3">Yesterday&apos;s recap.</p>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="bg-ink border border-white/20 text-white px-2 py-1.5 font-outfit text-sm"
          data-testid="daily-date-input"
        />
        <button
          onClick={runPreview}
          disabled={loading}
          className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 font-outfit font-bold uppercase tracking-widest text-xs flex items-center gap-1 disabled:opacity-50"
          data-testid="daily-preview-btn"
        >
          <Eye size={12} /> {loading ? "…" : "Preview"}
        </button>
        <button
          onClick={sendNow}
          disabled={sending}
          className="bg-hotpink hover:bg-hotpink/90 text-white px-3 py-1.5 font-outfit font-bold uppercase tracking-widest text-xs flex items-center gap-1 disabled:opacity-50"
          data-testid="daily-send-btn"
        >
          <Send size={12} /> {sending ? "…" : "Send Now"}
        </button>
      </div>
      {s && (
        <div className="text-sm font-outfit space-y-1" data-testid="daily-preview-body">
          <div className="text-white/60 text-xs">{s.date_local} · {s.tz}</div>
          <div className="brush-title text-2xl text-hotpink">${s.gross_revenue.toFixed(2)} → ${s.net_revenue.toFixed(2)}</div>
          <div className="text-white/70 text-xs">Tx: {s.paid_transactions} · Eps: {s.episodes_unlocked} · Signups: {s.new_signups}</div>
          {s.top_episodes?.slice(0, 3).map((t, i) => (
            <div key={t.number} className="flex justify-between border-b border-white/5 py-0.5 text-white text-xs">
              <span>#{i + 1} Ep {t.number} — {t.title}</span>
              <span className="text-hotpink font-bold">{t.unlocks}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WeeklyBlock() {
  const [weekStart, setWeekStart] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try {
      const params = weekStart ? { week_start: weekStart } : {};
      const { data } = await api.post("/admin/weekly-digest/preview", null, { params });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm(
      `Send WEEKLY digest NOW to: ${(preview?.recipients || ["admin@ifweknewaudio.com"]).join(", ")}?`
    )) return;
    setSending(true);
    try {
      const params = weekStart ? { week_start: weekStart } : {};
      const { data } = await api.post("/admin/weekly-digest/send-test", null, { params });
      if (data?.ok) toast.success(`Weekly sent to ${data.results?.length || 0} recipient(s)`);
      else toast.error(data?.reason || "Send failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const d = preview?.digest;
  const arrow = (delta) => {
    if (delta === null || delta === undefined) return <span className="text-white/40 text-xs">—</span>;
    const cls = delta >= 0 ? "text-green-400" : "text-red-400";
    const sign = delta >= 0 ? "▲" : "▼";
    return <span className={`${cls} text-xs font-bold`}>{sign} {Math.abs(delta).toFixed(1)}%</span>;
  };

  return (
    <div className="border border-white/10 bg-coal p-5" data-testid="weekly-block">
      <div className="flex items-center gap-2 mb-1">
        <CalendarRange size={16} className="text-hotpink" />
        <h3 className="brush-title text-2xl text-white">Weekly · Mon 9 AM ET</h3>
      </div>
      <p className="scribble text-white/50 text-xs mb-3">Prior 7 days + WoW deltas.</p>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <input
          type="date"
          value={weekStart}
          onChange={(e) => setWeekStart(e.target.value)}
          placeholder="Week start (Mon)"
          className="bg-ink border border-white/20 text-white px-2 py-1.5 font-outfit text-sm"
          data-testid="weekly-date-input"
        />
        <button
          onClick={runPreview}
          disabled={loading}
          className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 font-outfit font-bold uppercase tracking-widest text-xs flex items-center gap-1 disabled:opacity-50"
          data-testid="weekly-preview-btn"
        >
          <Eye size={12} /> {loading ? "…" : "Preview"}
        </button>
        <button
          onClick={sendNow}
          disabled={sending}
          className="bg-hotpink hover:bg-hotpink/90 text-white px-3 py-1.5 font-outfit font-bold uppercase tracking-widest text-xs flex items-center gap-1 disabled:opacity-50"
          data-testid="weekly-send-btn"
        >
          <Send size={12} /> {sending ? "…" : "Send Now"}
        </button>
      </div>
      {d && (
        <div className="text-sm font-outfit space-y-1" data-testid="weekly-preview-body">
          <div className="text-white/60 text-xs">{d.range_start_local} → {d.range_end_local} · {d.tz}</div>
          <div className="flex items-baseline gap-2">
            <div className="brush-title text-2xl text-hotpink">${d.gross_revenue.toFixed(2)}</div>
            {arrow(d.wow_deltas?.gross)}
            <span className="text-white/40 text-xs">vs prior wk ${d.prior_week?.gross_revenue.toFixed(2)}</span>
          </div>
          <div className="text-white/70 text-xs flex flex-wrap gap-3">
            <span>Tx: {d.paid_transactions} {arrow(d.wow_deltas?.paid_transactions)}</span>
            <span>Eps: {d.episodes_unlocked} {arrow(d.wow_deltas?.episodes_unlocked)}</span>
            <span>Signups: {d.new_signups} {arrow(d.wow_deltas?.new_signups)}</span>
          </div>
          {d.top_episodes?.slice(0, 3).map((t, i) => (
            <div key={t.number} className="flex justify-between border-b border-white/5 py-0.5 text-white text-xs">
              <span>#{i + 1} Ep {t.number} — {t.title}</span>
              <span className="text-hotpink font-bold">{t.unlocks}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DailyRevenueEmailPanel() {
  return (
    <div className="bg-ink border border-white/10 p-6" data-testid="daily-revenue-email-panel">
      <div className="flex items-center gap-2 mb-1">
        <Mail size={18} className="text-hotpink" />
        <h2 className="brush-title text-3xl text-white">Revenue Emails</h2>
      </div>
      <p className="scribble text-white/60 text-sm mb-4">
        Auto-recap emails to <span className="text-hotpink font-bold">admin@ifweknewaudio.com</span> · <span className="text-hotpink">DAILY_REVENUE_RECIPIENTS</span> env to change.
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        <DailyBlock />
        <WeeklyBlock />
      </div>
    </div>
  );
}

